import { Config, DataGrid, DateEditor, ErrorHandling, TranslationConfig, formatNumber, resolveUrl, serviceCall } from "@serenity-is/corelib"; // DataGrid kvůli defaultPersistenceStorage; formatNumber (denní vykázanost)
import { gridDefaults } from "@serenity-is/sleekgrid";
import { getLanguageList } from "./Helpers/LanguageList";
import { QuickCreateDialog } from "../HelpDesk/Tickets/QuickCreateDialog"; // CÍL A: „Spustit novou práci" + Ctrl+.
import { TicketTasksDialog } from "../HelpDesk/TicketTasks/TicketTasksDialog"; // proklik běžící položky → TicketTask
import { renderRunningTimerItem, updateElapsed } from "./RunningTimerActions"; // sdílené akce/render běžící položky
import { initThemeToggle } from "./ThemeToggle"; // přepínač světlý/tmavý režim (#s-theme-toggle)
import { ShiftsRow } from "../ServerTypes/WorkReports/ShiftsRow"; // typovaná pole pro IncludeColumns/EqualityFilter
import { ShiftsService } from "../ServerTypes/WorkReports/ShiftsService"; // dnešní směna → denní vykázanost v liště
// flatpickr = klikací date+time picker. JS se bundluje a vystavuje jako global
// (corelib testuje typeof flatpickr); CSS se sem ZÁMĚRNĚ NEimportuje — esbuild CSS chunk do stránky sám
// nevloží → flatpickr se vykresluje „nahý" v rohu. Flatpickr CSS se linkuje STATICKY v layoutu.
// NEpřidávej sem `import "flatpickr/dist/flatpickr.css"`.
import flatpickr from "flatpickr";
import { Czech } from "flatpickr/dist/l10n/cs.js"; // česká lokalizace kalendáře (dny/měsíce CZ)

Config.rootNamespaces.push('DigiDesk2025');
TranslationConfig.getLanguageList = getLanguageList;

// CKEditor: nic nenastavuj – HtmlContentEditor načítá z CDN ve výchozím stavu
gridDefaults.useCssVars = false;

// Persistence nastavení gridů (šířky/pořadí/viditelnost sloupců, řazení, quick filtry,
// quick search) do localStorage (ne serverové úložiště; UTILITA,
// ne společný předek gridů — projekt žádného nemá). Corelib má napojení plně automatické: jakmile
// defaultPersistenceStorage není null, KAŽDÝ grid si při initu volá restoreSettings a při změně
// (resize/reorder/sort/quick filtr) persistSettings; klíč = „GridSettings:<stránka>:<TřídaGridu>"
// (per stránka a grid — dva gridy nad touž Row se nepomíchají). Selhání localStorage (private mode,
// plné úložiště) nesmí shodit grid → try/catch, degraduje na „bez persistence".
// Reset rozložení = smazat klíče GridSettings:* v localStorage (DevTools).
DataGrid.defaultPersistenceStorage = {
    // '' místo null — SettingStorage.getItem typově nepřipouští null; prázdný string corelib bezpečně
    // ignoruje (parsuje jen JSON objekt '{…}')
    getItem: key => { try { return localStorage.getItem(key) ?? ''; } catch { return ''; } },
    setItem: (key, value) => { try { localStorage.setItem(key, value); } catch { /* plné/nedostupné úložiště — bez persistence */ } }
};

// Zapnout flatpickr (klikací kalendář date+time) u DateEditor/DateTimeEditor.
// Corelib použije flatpickr, IFF je `flatpickr` dostupný jako GLOBAL a (useFlatpickr || není jQuery
// datepicker) → proto ho vystavíme jako global a nastavíme českou lokalizaci. CSS se zde NEnačítá
// (viz import výše) — linkuje se staticky v layoutu, jinak je „nahý".
(window as any).flatpickr = flatpickr;
flatpickr.localize(Czech);
DateEditor.useFlatpickr = true;

window.onerror = ErrorHandling.runtimeErrorHandler;
window.addEventListener('unhandledrejection', ErrorHandling.unhandledRejectionHandler);

// ====== Globální indikátor běžících měření času v horní liště ======
// Žije v shellu (ScriptInit běží na KAŽDÉ stránce → přežije navigaci). Pravda je vždy v DB
// (běžící track = EndAt IS NULL); polluje TimeTracks/MyRunning (~30 s) + naslouchá eventu
// 'digidesk:timetracks-changed' (vysílá ho TicketTasksDialog po vlastním start/stop).
// Stop VÝHRADNĚ přes TicketTasks/StopTimeTrack (D9). Montuje se do <li id="s-running-timers">
// v _Sidebar.cshtml (jen pro přihlášené); pokud kotva chybí (login/bez navigace), nic neudělá.
function initRunningTimersIndicator() {
    const host = document.getElementById('s-running-timers');
    if (!host || host.dataset.init === '1')
        return;
    host.dataset.init = '1';

    let items: any[] = [];
    let tickId: any = 0;

    const wrap = document.createElement('div');
    wrap.className = 'dropdown';
    const link = document.createElement('a');
    link.href = 'javascript:;';
    link.className = 's-sidebar-header-link s-running-timers-link';
    link.setAttribute('data-bs-toggle', 'dropdown');
    link.title = 'Běžící měření času';
    const menu = document.createElement('div');
    menu.className = 'dropdown-menu dropdown-menu-md dropdown-menu-end s-running-timers-menu';
    wrap.append(link, menu);
    host.append(wrap);

    function renderLink() {
        // KLIDOVÝ stav hodin bez barevné třídy = bílá (jako sousední language/user ikony a „+"; text-muted by byl
        // málo kontrastní na modrém pozadí). BĚŽÍCÍ stav je
        // text-danger (červená) + červený badge — záměrný signál „běží ti čas".
        link.innerHTML = items.length > 0
            ? `<i class="fa fa-clock text-danger"></i><span class="badge bg-danger ms-1">${items.length}</span>`
            : `<i class="fa fa-clock"></i>`;
    }

    function renderMenu() {
        menu.replaceChildren();

        // „Spustit novou práci" je STATICKÉ tlačítko v horní liště
        // (#s-quick-create v _Sidebar.cshtml) → v tomto dropdownu není (jen seznam běžících měření).
        // Klávesová zkratka Ctrl+. funguje též.

        if (!items.length) {
            const e = document.createElement('div');
            e.className = 'dropdown-item-text text-muted small';
            e.textContent = 'Žádné běžící měření.';
            menu.append(e);
            return;
        }
        const head = document.createElement('div');
        head.className = 'dropdown-header';
        head.textContent = 'Běží ti čas';
        menu.append(head);
        // sdílený render + akce (Zastavit / Zastavit a vyřešit / Upravit / Zrušit) – JEDNA definice (RunningTimerActions)
        // proklik položky otevře TicketTask (TaskId), ke kterému je TimeTrack navázaný
        for (const it of items)
            menu.append(renderRunningTimerItem(it, taskId => { new TicketTasksDialog().loadByIdAndOpenDialog(taskId); }));
    }

    function restartTick() {
        if (tickId) { clearInterval(tickId); tickId = 0; }
        if (items.length)
            tickId = setInterval(() => updateElapsed(menu), 1000); // sdílená živá HH:MM:SS
    }

    function refresh() {
        serviceCall({
            url: resolveUrl('~/Services/HelpDesk/TimeTracks/MyRunning'),
            request: {},
            onSuccess: (r: any) => { items = r?.Items ?? []; renderLink(); renderMenu(); restartTick(); },
            onError: () => true // nepřihlášený / chyba – tiše ignoruj (indikátor je nepovinný)
        });
    }

    renderLink();
    refresh();
    setInterval(refresh, 30000);                                  // periodický polling
    window.addEventListener('digidesk:timetracks-changed', refresh); // okamžitý refresh po vlastním start/stop
}

// ====== CÍL A: „Spustit novou práci" = QuickCreate s rovnou spuštěným časem ======
// Volatelné z dropdownu i klávesovou zkratkou. Guard proti dvojímu QuickCreate přes DOM
// (otevřený dialog má CSS třídu s-HelpDesk-QuickCreateDialog — root namespace DigiDesk2025
// se stripuje dle Config.rootNamespaces). Po uložení vyšle event → indikátor se obnoví.
function openQuickCreate() {
    if (document.querySelector('.s-HelpDesk-QuickCreateDialog'))
        return; // už je otevřený – neotevírej druhý
    // v detailu eventu jede ticketId nového tiketu → MyTicketsGrid ho na 60 s zvýrazní (stejně jako z tlačítka
    // gridu); ostatní posluchači detail ignorují (indikátor jen obnoví MyRunning). Bez result (resolve/handover) = undefined.
    const dlg = new QuickCreateDialog({
        onDone: (result) => window.dispatchEvent(new CustomEvent('digidesk:timetracks-changed', { detail: { ticketId: result?.ticketId } }))
    });
    // auto-start měření je VÝCHOZÍ přímo v QuickCreateDialog.applyPreset() (platí pro
    // všechny otevírací cesty) – JEDNA definice
    // (anti-drift D9): default vlastní applyPreset, ScriptInit jen otevírá dialog.
    dlg.applyPreset();
    dlg.dialogOpen();
}

// (Akce „Upravit časy", Stop, Resolve nad běžícím trackem jsou ve sdíleném RunningTimerActions.)

// ====== CÍL A: klávesová zkratka Ctrl+. (tečka) – „spustit novou práci" odkudkoli ======
// Jeden listener na document (ScriptInit běží per page-load → žádná akumulace). Chord Ctrl + .
// (focus v inputu nevadí). preventDefault jen když akci skutečně spouštíme; guard proti dvojímu
// dialogu je v openQuickCreate (DOM check). Ctrl+. není standardní prohlížečová/Serenity zkratka.
document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.ctrlKey && !e.altKey && !e.metaKey && !e.repeat && (e.key === '.' || e.code === 'Period')) {
        e.preventDefault();
        openQuickCreate();
    }
});

// ====== Globální tlačítko Quick Create v horní liště (#s-quick-create, _Sidebar.cshtml) ======
// Klik = totéž co Ctrl+. (openQuickCreate). Idempotentní (guard data-init); zkratka Ctrl+. zůstává.
function initQuickCreateButton() {
    const btn = document.getElementById('s-quick-create');
    if (!btn || btn.dataset.init === '1')
        return;
    btn.dataset.init = '1';
    btn.addEventListener('click', e => { e.preventDefault(); openQuickCreate(); });
}

// ====== Statická denní vykázanost v horní liště ======
// Montuje se do <li id="s-day-utilization"> v _Sidebar.cshtml (jen přihlášení). STATICKÁ informace —
// JEDEN fetch při načtení stránky, žádný polling ani event („načte se při
// načtení stránky, ne live"). Zdroj = TENTÝŽ výpočet jako sloupec „Utilizace %" v MyShifts:
// expression pole ShiftsRow.DayUtilizationPct (JEDEN vzorec v SQL, anti-drift D9) — dotáhne se přes
// Shifts/List (MineOnly → @me vynucuje SERVER) + IncludeColumns (non-table pole se v List módu
// jinak neselectuje). Bez dnešní směny / fond 0 (pct NULL) / bez práva WorkReports:View →
// nevykreslí se nic (údaj je nepovinný, lišta zůstává čistá).
//
// Dnešní řádek se NEHLEDÁ datovým filtrem:
// EqualityFilter na DateOnly poli padá na SERVERU — ListRequestHandler volá field.ConvertValue →
// Convert.ChangeType(String→DateOnly) = InvalidCastException (DateOnly není IConvertible; ověřeno
// ve zdrojích frameworku, 03 §12). Criteria rovnost by prošla jen díky spolknuté
// výjimce v CriteriaFieldExpressionReplacer (catch „backward compatibility") + implicitní SQL
// konverzi — vědomě NEpoužito. Místo toho kombinace parametrů BEZ hodnotových konverzí, které
// MyShifts grid posílá v produkci denně: FromToday (server: WorkDate >= DateTime.Today, čisté C#
// v ShiftsListHandler) + Sort WorkDate + Take 1 → první řádek = dnešek NEBO nejbližší budoucí den;
// rovnost s dneškem ověří KLIENT (DateOnly se serializuje 'yyyy-MM-dd'; substring(0,10) je pojistka
// pro případnou časovou složku). Neshoda = dnešní směna neexistuje → nic se nevykreslí.
function initDayUtilization() {
    const host = document.getElementById('s-day-utilization');
    if (!host || host.dataset.init === '1')
        return;
    host.dataset.init = '1';

    const d = new Date(); // lokální dnešek (server i klient jedou na lokálním čase — vzor TTR odpočtu)
    const today = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    ShiftsService.List({
        MineOnly: true,
        FromToday: true, // místo datové rovnosti (viz komentář výše)
        Sort: [ShiftsRow.Fields.WorkDate],
        Take: 1,
        IncludeColumns: [ShiftsRow.Fields.DayUtilizationPct, ShiftsRow.Fields.WorkDate]
    }, r => {
        const row = r?.Entities?.[0] as ShiftsRow;
        if (!row || (row.WorkDate ?? '').substring(0, 10) !== today)
            return; // žádná dnešní směna (první řádek ≥ dnešek je budoucí den) → nic
        const pct = row.DayUtilizationPct;
        if (pct == null)
            return; // nulový fond (ScheduledHours 0/NULL) → nic
        // Zarovnání a velikost: sousedi nesou ikonu <i class="fa"> (glyph centrovaný na řádek), tenhle prvek holý text —
        // pozici srovná inline-flex + align-items + height:100%, velikost textu by se ale dědila z kontejneru
        // lišty (theme CSS není v repu — Serenity.Assets RCL). Proto velikost i řádkování NATVRDO
        // na prvku: fontSize 1rem (kdyby nesedlo vůči ikonám: zkusit '0.875rem' / '14px') +
        // lineHeight 1.
        const link = document.createElement('a');
        link.href = 'javascript:;';
        link.className = 's-sidebar-header-link s-day-utilization-value';
        link.style.display = 'inline-flex';
        link.style.alignItems = 'center';
        link.style.height = '100%';
        link.style.fontSize = '1rem'; // přebít děděnou velikost
        link.style.lineHeight = '1';
        link.style.cursor = 'default'; // není to akce, jen informace
        link.textContent = 'Dnes ' + formatNumber(pct, '#,##0') + ' %'; // celá % (sladěno se sloupcem)
        link.title = 'Denní vykázanost: vykázaný čas dnes (ukončená měření mimo režii) / denní fond (Scheduled h). ' +
            'Statický údaj — načítá se při otevření stránky. Detail po dnech: My Shifts, sloupec „Utilizace %“.'; // O-1: česká uzavírací uvozovka
        host.append(link);
    }, { onError: () => true }); // nepřihlášený / bez práva WorkReports / chyba — tiše nic (údaj je nepovinný)
}

// ====== počítadla v postranním menu ======
// Badge u odkazů menu: „Absence" (žádosti ke schválení; 🔒 červeně jako eskalace — i na rodiči
// WorkReports), „Moje tikety" (červeně mé rozdělané eskalované tikety; TYRKYSOVĚ rozdělané tikety bez hlavního řešitele —
// hned vedle červeného, jen uživatelům, kterým je Moje tikety ukazuje) a „Manager" (tikety po lhůtě fakturace, bg-danger,
// jen uživatelé s HelpDesk:Approve — server vrací null ostatním). Zdroj = JEDEN endpoint Common/ShellCounters/Get (server rozhodne
// podle práv, null = nekreslit); polling 30 s jako indikátor běžících měření. Odkazy se hledají podle konce href (menu =
// NavigationLink → ~/… vyřešené na kořen aplikace); když odkaz v menu není (uživatel bez práva), badge se nekreslí. Chyby
// (nepřihlášený, výpadek) se tiše ignorují — signál je nepovinný. Hodnota 0 / null badge odstraní.
function initShellCounters() {
    const absencesLink = document.querySelector<HTMLAnchorElement>('a.s-sidebar-link[href$="/WorkReports/Absences"]');
    const ticketsLink = document.querySelector<HTMLAnchorElement>('a.s-sidebar-link[href$="/HelpDesk/MyTickets"]');
    const managerLink = document.querySelector<HTMLAnchorElement>('a.s-sidebar-link[href$="/HelpDesk/Manager"]');
    if (!absencesLink && !ticketsLink && !managerLink)
        return;
    if (document.body.dataset.shellCounters === '1')
        return;
    document.body.dataset.shellCounters = '1';

    // key rozlišuje víc badge na jednom odkazu (Moje tikety: 'main' červený + 'unassigned' tyrkysový); pořadí je
    // stabilní — tyrkysový je vždy ZA červeným, i když se červený objeví později
    const setBadge = (link: HTMLAnchorElement | null, value: number | null | undefined, cls: string, title: string,
        key = 'main') => {
        if (!link)
            return;
        let badge = link.querySelector<HTMLElement>('.s-shell-counter[data-key="' + key + '"]');
        if (value == null || value <= 0) {
            badge?.remove();
            return;
        }
        if (!badge) {
            badge = document.createElement('span');
            badge.className = 's-shell-counter badge ms-1 ' + cls;
            badge.dataset.key = key;
            const unassigned = link.querySelector<HTMLElement>('.s-shell-counter[data-key="unassigned"]');
            if (key === 'main' && unassigned)
                link.insertBefore(badge, unassigned);
            else
                link.append(badge);
        }
        badge.textContent = String(value);
        badge.title = title;
    };

    // TÝŽ badge i na RODIČOVSKÉ položce (WorkReports) — sbalená sekce by upozornění schovala. Rodič se hledá
    // STABILNÍM DOM vztahem od odkazu potomka: potomek leží v ul.s-sidebar-menu.collapse uvnitř li.s-sidebar-item.has-children, jeho
    // odkaz je přímý a.s-sidebar-link toho li (_Sidebar.cshtml). 🛡 ⛔ Nikdy podle lokalizovaného textu menu; ⛔ rodič se nerozbaluje.
    // Badge na rodiči se ukazuje vždy (rozbalený i sbalený) — bez naslouchání collapse událostem, ať rozložení nemění pod rukama.
    const parentLinkOf = (link: HTMLAnchorElement | null): HTMLAnchorElement | null => {
        const menu = link?.closest('ul.s-sidebar-menu');
        const parentLi = menu?.parentElement;
        if (!parentLi || !parentLi.matches('li.s-sidebar-item.has-children'))
            return null;
        return parentLi.querySelector(':scope > a.s-sidebar-link') as HTMLAnchorElement;
    };
    const absencesParentLink = parentLinkOf(absencesLink);

    const refresh = () => {
        serviceCall({
            url: resolveUrl('~/Services/Common/ShellCounters/Get'),
            request: {},
            onSuccess: (r: any) => {
                // 🔒 odznak absencí ČERVENĚ (bg-danger) — stejná barva jako eskalované MyTickets, u Absences i u rodiče WorkReports
                setBadge(absencesLink, r?.AbsencesPending, 'bg-danger', 'Žádosti o absenci čekající na schválení');
                setBadge(absencesParentLink, r?.AbsencesPending, 'bg-danger', 'Žádosti o absenci čekající na schválení (Absence)');
                setBadge(ticketsLink, r?.MyTicketsEscalated, 'bg-danger', 'Moje rozdělané tikety s eskalací');
                // tyrkysové počítadlo tiketů bez hlavního řešitele (server vrací jen tomu, komu je Moje tikety ukazují);
                // tyrkysově = bg-info s tmavým textem (7,9:1 v obou režimech; bílý text by na #0dcaf0 měl jen 1,9:1) — týž tón
                // jako štítek „bez řešitele" ve sloupci Stav Mojich tiketů (mytickets-nosolver-badge)
                setBadge(ticketsLink, r?.MyTicketsUnassigned, 'bg-info text-dark',
                    'Rozdělané tikety bez hlavního řešitele (čekají na přidělení)', 'unassigned');
                // tikety po lhůtě fakturace (jen manažer — server jinak vrací null)
                setBadge(managerLink, r?.ManagerTicketsOverdue, 'bg-danger', 'Tikety po lhůtě fakturace — nejstarší práce začala před více než 6 měsíci; uzavři je co nejdřív');
            },
            onError: () => true // nepřihlášený / chyba – tiše ignoruj (počítadla jsou nepovinná)
        });
    };

    refresh();
    setInterval(refresh, 30000);
}

function initHeaderActions() {
    initRunningTimersIndicator();
    initQuickCreateButton();
    initDayUtilization(); // statická denní vykázanost
    initThemeToggle(); // přepínač světlý/tmavý režim
    initShellCounters(); // počítadla v menu (Absence + WorkReports, Moje tikety, Manager)
}

if (document.readyState !== 'loading')
    initHeaderActions();
else
    document.addEventListener('DOMContentLoaded', initHeaderActions);
